import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Bot, Radar, Send, Trash2, Search, Mail, Upload, FileSpreadsheet, CheckCircle2, Ban, Activity, Copy, Check } from "lucide-react";

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700",
  invited: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  stopped: "bg-slate-200 text-slate-600",
  bounced: "bg-orange-100 text-orange-700",
  unsubscribed: "bg-slate-200 text-slate-500",
};

export default function OutreachAdmin() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [urls, setUrls] = useState("");
  const [hint, setHint] = useState("");
  const [journals, setJournals] = useState([]);
  const [journalSlug, setJournalSlug] = useState("any");
  const [discovering, setDiscovering] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [counts, setCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [siteStats, setSiteStats] = useState([]);
  const [runningNow, setRunningNow] = useState(false);
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailDraft, setEmailDraft] = useState({});
  const [savingEmail, setSavingEmail] = useState({});
  const [copiedTitle, setCopiedTitle] = useState(null);

  const loadProspects = useCallback(async () => {
    const { data } = await api.get("/admin/outreach/prospects", { params: filter ? { status: filter } : {} });
    setProspects(data.prospects || []);
    setCounts(data.counts || {});
    setStats(data.stats || null);
  }, [filter]);

  useEffect(() => {
    api.get("/admin/outreach/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);
  useEffect(() => { api.get("/journals").then(({ data }) => setJournals(data || [])).catch(() => {}); }, []);
  const loadRuns = useCallback(async () => {
    try { const { data } = await api.get("/admin/outreach/runs"); setRuns(data.runs || []); setSiteStats(data.sites || []); } catch (e) { console.error("Failed to load outreach runs:", e); }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { if (user?.email && !testTo) setTestTo(user.email); }, [user, testTo]);
  useEffect(() => { setLoading(true); loadProspects().finally(() => setLoading(false)); }, [loadProspects]);

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      const { data } = await api.put("/admin/outreach/config", next);
      setCfg(data);
      toast.success("Outreach settings saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingCfg(false); }
  };

  const discover = async () => {
    const list = urls.split(/[\n,]/).map((u) => u.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one website link."); return; }
    setDiscovering(true);
    try {
      const { data } = await api.post("/admin/outreach/discover", { urls: list, journal_hint: hint, journal_slug: journalSlug === "any" ? "" : journalSlug });
      toast.success(`Found ${data.discovered} authors — ${data.added} new prospect(s) added.`);
      if (data.errors?.length) toast.warning(`${data.errors.length} link(s) could not be read.`);
      await loadProspects();
      await loadRuns();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setDiscovering(false); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("journal_hint", hint || "");
      fd.append("journal_slug", journalSlug === "any" ? "" : journalSlug);
      const { data } = await api.post("/admin/outreach/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.added} prospect(s) from ${data.rows} row(s). ${data.skipped ? data.skipped + " skipped." : ""}`);
      await loadProspects();
      await loadRuns();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setImporting(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter an email to send the test to."); return; }
    setTestBusy(true);
    try {
      await api.post("/admin/outreach/test-email", { to: testTo });
      toast.success(`Test invitation sent to ${testTo}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setTestBusy(false); }
  };

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    setSending(true);
    try {
      const { data } = await api.post("/admin/outreach/send", { prospect_ids: ids.length ? ids : null });
      toast.success(`Sent ${data.sent} invitation(s). ${data.skipped ? data.skipped + " skipped." : ""}`);
      setSelected({});
      await loadProspects();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/outreach/prospects/${id}`); await loadProspects(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markInvalid = async (id) => {
    try { await api.post(`/admin/outreach/prospects/${id}/invalid`); await loadProspects(); toast.success("Marked as invalid — the agent will stop emailing this address."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEmail = async (id) => {
    const email = (emailDraft[id] ?? "").trim();
    setSavingEmail((s) => ({ ...s, [id]: true }));
    try {
      await api.patch(`/admin/outreach/prospects/${id}`, { email });
      setEmailDraft((d) => { const n = { ...d }; delete n[id]; return n; });
      await loadProspects();
      toast.success(email ? "Email saved." : "Email cleared.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEmail((s) => ({ ...s, [id]: false })); }
  };

  const copyTitle = (title) => {
    if (!title) return;
    navigator.clipboard?.writeText(title);
    setCopiedTitle(title);
    toast.success("Paper title copied.");
    setTimeout(() => setCopiedTitle(null), 1500);
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      await api.post("/admin/outreach/run-now");
      toast.success("Run started — scanning your sites and sending. The activity log will update shortly.");
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        await loadRuns();
        await loadProspects();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunningNow(false); }
  };

  if (!cfg) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const enabled = cfg.enabled === true;

  return (
    <div data-testid="outreach-admin" className="max-w-4xl">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
        <Bot className="w-6 h-6 text-amber-600" /> AI Author Outreach
      </h1>
      <p className="text-slate-500 text-sm mb-6">
        A private agent that automatically scans the websites you add, finds researchers and their emails, picks the
        best-fit Noventra journal for each author, and emails them personalised invitations — hands-free. This panel
        is not visible anywhere on the public site.
      </p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="outreach-stats">
          {[
            { l: "Invited", v: stats.sent_prospects },
            { l: "Emails sent", v: stats.emails_sent },
            { l: "Opened", v: stats.opened },
            { l: "Submitted", v: stats.submitted, sub: `${stats.conversion}% conversion` },
          ].map((s) => (
            <div key={s.l} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{s.v}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.l}{s.sub ? ` · ${s.sub}` : ""}</p>
            </div>
          ))}
        </div>
      )}

      {/* Master control */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-base font-semibold">Outreach agent</Label>
            <p className="text-xs text-slate-500 mt-0.5">Master switch. When ON, the agent automatically scans your monitored websites daily, emails new authors, and re-invites those past the gap. When OFF, nothing is sent.</p>
          </div>
          <div className="flex items-center gap-2">
            {savingCfg && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <Switch checked={enabled} onCheckedChange={(v) => saveCfg({ enabled: v })} data-testid="outreach-enabled-switch" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <Label className="text-sm">Max invitations per day</Label>
            <Input type="number" min={1} max={500} value={cfg.max_per_day ?? 50} onChange={(e) => setCfg({ ...cfg, max_per_day: Number(e.target.value) })} onBlur={(e) => saveCfg({ max_per_day: Number(e.target.value) })} className="mt-1.5" data-testid="outreach-max-per-day" />
          </div>
          <div>
            <Label className="text-sm">Resend gap (days)</Label>
            <Input type="number" min={1} max={90} value={cfg.resend_gap_days ?? 7} onChange={(e) => setCfg({ ...cfg, resend_gap_days: Number(e.target.value) })} onBlur={(e) => saveCfg({ resend_gap_days: Number(e.target.value) })} className="mt-1.5" data-testid="outreach-gap-days" />
          </div>
          <div>
            <Label className="text-sm">Max attempts per author</Label>
            <Input type="number" min={1} max={10} value={cfg.max_attempts ?? 3} onChange={(e) => setCfg({ ...cfg, max_attempts: Number(e.target.value) })} onBlur={(e) => saveCfg({ max_attempts: Number(e.target.value) })} className="mt-1.5" data-testid="outreach-max-attempts" />
          </div>
          <div>
            <Label className="text-sm">Reply-to email</Label>
            <Input type="email" placeholder="editorial@noventrapress.org" value={cfg.reply_to ?? ""} onChange={(e) => setCfg({ ...cfg, reply_to: e.target.value })} onBlur={(e) => saveCfg({ reply_to: e.target.value })} className="mt-1.5" data-testid="outreach-reply-to" />
          </div>
        </div>
      </div>

      {/* Email template */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
        <h2 className="font-serif font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-amber-600" /> Invitation email</h2>
        <p className="text-slate-500 text-sm mt-1">
          Customise the message for this outreach batch. Use placeholders{" "}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{name}"}</code>,{" "}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{paper_title}"}</code> and{" "}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{journal}"}</code>. Separate paragraphs with a blank line.
        </p>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-sm">Subject line</Label>
            <Input value={cfg.email_subject ?? ""} onChange={(e) => setCfg({ ...cfg, email_subject: e.target.value })} onBlur={(e) => saveCfg({ email_subject: e.target.value })} className="mt-1.5" data-testid="outreach-email-subject" />
          </div>
          <div>
            <Label className="text-sm">Heading</Label>
            <Input value={cfg.email_heading ?? ""} onChange={(e) => setCfg({ ...cfg, email_heading: e.target.value })} onBlur={(e) => saveCfg({ email_heading: e.target.value })} className="mt-1.5" data-testid="outreach-email-heading" />
          </div>
          <div>
            <Label className="text-sm">Message body</Label>
            <Textarea rows={9} value={cfg.email_body ?? ""} onChange={(e) => setCfg({ ...cfg, email_body: e.target.value })} onBlur={(e) => saveCfg({ email_body: e.target.value })} className="mt-1.5 text-sm" data-testid="outreach-email-body" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-sm">Send a test invitation to</Label>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="mt-1.5" data-testid="outreach-test-to" />
          </div>
          <Button onClick={sendTest} disabled={testBusy} variant="outline" className="rounded-full gap-2" data-testid="outreach-test-btn">
            {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
          </Button>
        </div>
      </div>

      {/* Monitored websites (automatic) */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
        <div>
          <Label className="text-base font-semibold flex items-center gap-2"><Radar className="w-4 h-4 text-amber-600" /> Monitored websites (automatic)</Label>
          <p className="text-xs text-slate-500 mt-0.5">While the agent is ON, it scans these links once a day, adds any new authors it finds, and emails them automatically. The AI picks the best-fit journal unless you force one below.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <Label className="text-sm">Daily scan hour (UTC, 0–23)</Label>
            <Input type="number" min={0} max={23} value={cfg.schedule_hour ?? 8} onChange={(e) => setCfg({ ...cfg, schedule_hour: Number(e.target.value) })} onBlur={(e) => saveCfg({ schedule_hour: Number(e.target.value) })} className="mt-1.5" data-testid="outreach-schedule-hour" />
          </div>
          <div>
            <Label className="text-sm">Journal</Label>
            <Select value={cfg.schedule_journal_slug || "any"} onValueChange={(v) => saveCfg({ schedule_journal_slug: v === "any" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="outreach-schedule-journal"><SelectValue placeholder="Auto — AI picks best fit" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Auto — AI picks best fit</SelectItem>
                {journals.map((j) => <SelectItem key={j.slug} value={j.slug}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-sm">Websites to monitor (one per line)</Label>
          <Textarea rows={3} value={cfg.schedule_urls ?? ""} onChange={(e) => setCfg({ ...cfg, schedule_urls: e.target.value })} onBlur={(e) => saveCfg({ schedule_urls: e.target.value })} placeholder={"https://example.edu/faculty\nhttps://journal.org/latest-issue"} className="mt-1.5 font-mono text-sm" data-testid="outreach-schedule-urls" />
        </div>
        <div className="mt-4">
          <Label className="text-sm">Focus / topic hint (optional)</Label>
          <Input value={cfg.schedule_hint ?? ""} onChange={(e) => setCfg({ ...cfg, schedule_hint: e.target.value })} onBlur={(e) => saveCfg({ schedule_hint: e.target.value })} className="mt-1.5" data-testid="outreach-schedule-hint" />
        </div>
      </div>

      {/* Discover + import */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
        <h2 className="font-serif font-bold text-lg flex items-center gap-2"><Radar className="w-5 h-5 text-amber-600" /> Add prospects</h2>
        <p className="text-slate-500 text-sm mt-1">Paste website / platform links (one per line). The agent reads each page and extracts author names, paper titles and emails. Or import a CSV you already have.</p>
        <Textarea rows={4} value={urls} onChange={(e) => setUrls(e.target.value)} placeholder={"https://example.edu/faculty\nhttps://journal.org/latest-issue"} className="mt-3 font-mono text-sm" data-testid="outreach-urls" />
        <div className="mt-3">
          <Label className="text-sm">Focus / journal hint (optional)</Label>
          <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="e.g. computer science, materials engineering" className="mt-1.5" data-testid="outreach-hint" />
        </div>
        <div className="mt-3">
          <Label className="text-sm">Target journal (Auto lets the AI pick the best fit per author)</Label>
          <Select value={journalSlug} onValueChange={setJournalSlug}>
            <SelectTrigger className="mt-1.5" data-testid="outreach-journal-select"><SelectValue placeholder="Auto — AI picks best fit" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Auto — AI picks best fit</SelectItem>
              {journals.map((j) => <SelectItem key={j.slug} value={j.slug}>{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={discover} disabled={discovering} className="rounded-full gap-2" data-testid="outreach-discover-btn">
            {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Discover prospects
          </Button>
          <Button asChild disabled={importing} variant="outline" className="rounded-full gap-2" data-testid="outreach-import-btn">
            <label className="cursor-pointer">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Import CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="outreach-import-input" />
            </label>
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">CSV columns: <code>name</code>, <code>email</code>, <code>paper_title</code> (header row required).</p>
      </div>

      {/* Activity & sources */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6" data-testid="outreach-activity">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif font-bold text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-amber-600" /> Activity &amp; sources</h2>
          <Button onClick={runNow} disabled={runningNow} className="rounded-full gap-2" data-testid="outreach-run-now-btn">
            {runningNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Scan &amp; send now
          </Button>
        </div>
        {siteStats.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Per-site results (all runs)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-800"><th className="py-2 pr-4">Website</th><th className="py-2 pr-4">Runs</th><th className="py-2 pr-4">Authors found</th><th className="py-2">Added</th></tr></thead>
                <tbody>
                  {siteStats.map((s) => (
                    <tr key={s.url} className="border-b border-slate-100 dark:border-slate-800/60" data-testid={`outreach-site-${s.url}`}>
                      <td className="py-2 pr-4 text-slate-600 max-w-[320px] truncate" title={s.url}>{s.url}</td>
                      <td className="py-2 pr-4">{s.runs}</td>
                      <td className="py-2 pr-4 font-medium">{s.found}</td>
                      <td className="py-2">{s.added}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="text-xs uppercase tracking-wide text-slate-400 mt-5 mb-2">Recent runs</p>
        {runs.length === 0 ? (
          <p className="text-slate-500 text-sm">No runs yet. The agent logs a run each time it scans your sites or you discover prospects.</p>
        ) : (
          <ul className="space-y-2">
            {runs.map((r) => (
              <li key={r.id} className="flex items-start gap-3 text-sm border-l-2 border-amber-400 pl-3" data-testid={`outreach-run-${r.id}`}>
                <span className="text-slate-400 whitespace-nowrap text-xs mt-0.5">{new Date(r.at).toLocaleString()}</span>
                <span className="text-slate-600">
                  <span className="capitalize font-medium text-slate-800 dark:text-slate-200">{r.type}</span> run · scanned {r.sites?.length || 0} site(s) · found {r.found_total} author(s) · added {r.added_total} · sent {r.sent}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Prospects */}
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-serif font-bold text-lg">Prospects {counts.responded ? <span className="text-sm font-normal text-green-600">· {counts.responded} submitted</span> : null}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {["", "new", "invited", "responded", "bounced", "unsubscribed", "failed", "stopped"].map((st) => (
              <button key={st || "all"} onClick={() => setFilter(st)} data-testid={`outreach-filter-${st || "all"}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === st ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {st === "" ? "All" : st === "responded" ? `submitted (${counts.responded ?? 0})` : `${st} (${counts[st] ?? 0})`}
              </button>
            ))}
          </div>
        </div>

        <Button onClick={send} disabled={sending || !enabled} className="rounded-full gap-2 mb-4" data-testid="outreach-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {Object.values(selected).some(Boolean) ? "Send to selected" : "Send to all new"}
        </Button>
        {!enabled && <p className="text-xs text-amber-600 mb-4">Enable the agent above to send invitations.</p>}

        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : prospects.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">No prospects yet. Use “Discover prospects” or “Import CSV” above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 pr-2"></th>
                  <th className="py-2 pr-4">Author</th>
                  <th className="py-2 pr-4">Paper</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Sent</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800/60" data-testid={`outreach-row-${p.id}`}>
                    <td className="py-2.5 pr-2">
                      <input type="checkbox" checked={!!selected[p.id]} onChange={(e) => setSelected({ ...selected, [p.id]: e.target.checked })} data-testid={`outreach-select-${p.id}`} />
                    </td>
                    <td className="py-2.5 pr-4 font-medium text-slate-800 dark:text-slate-200">{p.name}</td>
                    <td className="py-2.5 pr-4 text-slate-500">
                      <div className="flex items-center gap-1.5 max-w-[240px]">
                        <span className="truncate" title={p.paper_title}>{p.paper_title || "—"}</span>
                        {p.paper_title && (
                          <button type="button" onClick={() => copyTitle(p.paper_title)} title="Copy full paper title" data-testid={`outreach-copy-title-${p.id}`} className="shrink-0 text-slate-400 hover:text-primary">
                            {copiedTitle === p.paper_title ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="email"
                          value={emailDraft[p.id] ?? p.email ?? ""}
                          placeholder="paste email…"
                          onChange={(e) => setEmailDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEmail(p.id); }}
                          className="h-8 text-xs w-[190px]"
                          data-testid={`outreach-email-input-${p.id}`}
                        />
                        {(emailDraft[p.id] !== undefined && emailDraft[p.id] !== (p.email ?? "")) && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" disabled={savingEmail[p.id]} onClick={() => saveEmail(p.id)} title="Save email" data-testid={`outreach-email-save-${p.id}`}>
                            {savingEmail[p.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4 text-green-600" />}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-1.5">
                        <Badge className={`${STATUS_STYLES[p.status] || ""} capitalize`}>{p.status}</Badge>
                        {p.responded && <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium" data-testid={`outreach-responded-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> submitted</span>}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-slate-400">{p.attempts ? `${p.attempts}×` : "—"}{p.last_sent_at ? ` · ${new Date(p.last_sent_at).toLocaleDateString()}` : ""}</td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      {p.email && p.status !== "bounced" && (
                        <Button size="icon" variant="ghost" onClick={() => markInvalid(p.id)} title="Mark email invalid / bounced" data-testid={`outreach-invalid-${p.id}`}><Ban className="w-4 h-4 text-orange-400" /></Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`outreach-delete-${p.id}`}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
